import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  checkResultFromError,
  type CanonicalRecord,
  type CheckResult,
  type EFile,
  type EFileDetail,
  type Upload,
  type UploadFormat,
} from '@tabservice/tabfile-client';
import { ArrowLeft, ArrowRight, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, LabelValue, LabelValueList, SectionHeader } from '@/components/ui/card';
import { Field, Textarea } from '@/components/ui/field';
import { ErrorNote, Spinner, TableSkeleton } from '@/components/ui/feedback';
import { Pill } from '@/components/ui/pill';
import { Segmented } from '@/components/ui/tabs';
import { CheckIssues, CheckSummary } from '@/features/new-filing/check-report';
import { formatFromFileName, formatMeta } from '@/features/new-filing/formats';
import {
  CheckingNote,
  ChosenFile,
  FileDropArea,
  FileInput,
  PassedNote,
  PrecheckIssues,
  ReviewFooter,
  ReviewParts,
  usePrecheck,
  useReviewParts,
} from '@/features/new-filing/file-review';
import { newIdempotencyKey } from '@/features/new-filing/idempotency';
import {
  NotFiledYet,
  WizardRail,
  amendStages,
  type StageTimes,
} from '@/features/new-filing/wizard-rail';
import { contentTypeFor, putFile } from '@/features/new-filing/file-preview';
import { UploadStatusPill } from '@/features/filings/status-pill';
import { AcceptedRecordsPanel, RejectedTransmissionsPanel } from './reference-panels';
import { uploadQuery } from '@/lib/queries';
import { errorMessage, tabfile } from '@/lib/api';
import { formatCount } from '@/lib/format';

export type AmendKind = 'correction' | 'replacement';

type Step = 'records' | 'checks';

interface Copy {
  /** The submit verb. It reads the same in the button, the toast and the tour. */
  action: string;
  recordsTitle: string;
  recordsDescription: string;
  jsonHint: string;
  submitDescription: string;
}

const copy: Record<AmendKind, Copy> = {
  correction: {
    action: 'Submit corrections',
    recordsTitle: 'Corrected records',
    recordsDescription:
      'Each record must carry the same recordRef as the accepted record it corrects, and Tab Service adds the IRS references from its own e-file history.',
    jsonHint: 'An array of corrected records. The recordRef on each one names the accepted record it corrects.',
    submitDescription:
      'A correction is a new e-file that points back at this one. The original stays on record and the correction is what the IRS reads.',
  },
  replacement: {
    action: 'Submit replacement',
    recordsTitle: 'Fixed records',
    recordsDescription:
      'The IRS rejected this e-file, so none of it was filed. Send the fixed records for the rejected transmissions and Tab Service resends them against the rejected receipt.',
    jsonHint: 'An array of fixed records, using the same recordRef values as the rejected transmission.',
    submitDescription:
      'A replacement resends the rejected transmissions inside the IRS grace window. It replaces them rather than adding to them.',
  },
};

const SAMPLE = `[
  {
    "recordRef": "nec-2026-00001",
    "FILER_FEDERAL_ID_NUMBER": "123456789",
    "RECIPIENT_ID_NUMBER": "987654321",
    "RECIPIENT_FIRST_NAME": "Marisol",
    "RECIPIENT_LAST_NAME": "Okafor",
    "RECIPIENT_STATE": "WI",
    "BOX1_NONEMPLOYEE_COMPENSATION": 4200
  }
]`;

const ACCEPTED_EXTENSIONS = '.csv,.xlsx,.xls,.txt,.dat,.xml';

/**
 * Corrections and replacements are the same two steps: assemble the records,
 * then read what the checks say before anything is sent. Only the wording, the
 * reference panel and the endpoint differ, so one component carries both.
 */
export function AmendFlow({
  kind,
  filing,
  resumeUploadId,
  onSubmitted,
}: {
  kind: AmendKind;
  filing: EFileDetail;
  /** A draft of this amendment, reopened from the Drafts list (TS-8288). */
  resumeUploadId?: string;
  onSubmitted: (created: EFile) => void;
}) {
  const words = copy[kind];

  const [view, setView] = useState<'file' | 'json'>('file');
  // A draft reopened from the Drafts list already holds its file, so it picks up at the
  // validation it was left at rather than asking for the file again (TS-8288).
  const [step, setStep] = useState<Step>(resumeUploadId ? 'checks' : 'records');
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [text, setText] = useState(SAMPLE);
  const [parseError, setParseError] = useState<string | null>(null);
  const [uploadId, setUploadId] = useState<string | null>(resumeUploadId ?? null);
  const [percent, setPercent] = useState<number | null>(null);
  const [inlineResult, setInlineResult] = useState<CheckResult | null>(null);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [stageTimes, setStageTimes] = useState<StageTimes>({});
  const idempotencyKey = useRef(newIdempotencyKey());

  // Stamp every stage this flow has moved past, once each - the same rule the new e-file
  // wizard follows, so a correction and a first filing read the same down the side.
  useEffect(() => {
    const reached = amendStages.findIndex((item) => item.id === step);
    setStageTimes((current) => {
      let next = current;
      amendStages.forEach((item, index) => {
        if (reached <= index || next[item.id]) return;
        next = { ...next, [item.id]: new Date().toISOString() };
      });
      return next;
    });
  }, [step]);

  const queryClient = useQueryClient();
  const upload = useQuery({
    ...uploadQuery(uploadId ?? ''),
    enabled: Boolean(uploadId),
    refetchInterval: (item) => {
      const status = item.state.data?.status;
      return status === 'checked' || status === 'blocked' || status === 'failed' || status === 'filed' ? false : 1200;
    },
  });
  const uploadData = upload.data as Upload | undefined;

  /**
   * The same review a new e-file gets before its upload: the file is read, mapped, checked
   * and totalled here, and the upload waits until the person has seen it. The amended
   * e-file settles the form and the year, so the check runs against those.
   */
  const chosenFormat = file ? formatFromFileName(file.name) : null;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const check = usePrecheck(file, {
    format: chosenFormat,
    formType: chosenFormat === 'csv' || chosenFormat === 'xlsx' ? filing.formType : null,
    taxYear: filing.taxYear,
    paused: Boolean(fileError),
  });
  const reviewState = useReviewParts(file, check.review);

  function parseRecords(): CanonicalRecord[] | null {
    try {
      const parsed: unknown = JSON.parse(text);
      if (!Array.isArray(parsed)) {
        setParseError('Send an array of records, even if there is only one.');
        return null;
      }
      if (parsed.length === 0) {
        setParseError('There are no records in that array.');
        return null;
      }
      if (parsed.length > 1000) {
        setParseError('That is more than 1,000 records. Upload a file instead.');
        return null;
      }
      setParseError(null);
      return parsed as CanonicalRecord[];
    } catch (error) {
      setParseError(error instanceof Error ? `That is not valid JSON: ${error.message}` : 'That is not valid JSON.');
      return null;
    }
  }

  function chooseFile(next: File) {
    setFile(next);
    setRequestError(null);
    // A different file is a different upload: the previous one's verdict, its refusal and
    // its transfer state must not be what this screen goes on showing (TS-8289).
    setUploadId(null);
    setPercent(null);
    setInlineResult(null);
    setFileError(refusal(formatFromFileName(next.name)));
  }

  /**
   * Why this screen cannot take the file, or null when it can.
   *
   * A complete IRIS transmission says in its own manifest what it is and which transmission
   * it refers to, so it is filed as it stands rather than assembled from rows here - the API
   * has always refused one on this path. The file was accepted all the way to a green
   * validation summary and an enabled button before saying so, which read as a fault in the
   * file. It is refused at the moment it is chosen now, and told where to go instead.
   */
  function refusal(format: UploadFormat | null): string | null {
    if (!format) {
      return 'Tab Service does not read that file type. Send a CSV, Excel or IRS 1220 file.';
    }
    if (format === 'irisxml') {
      return (
        `An IRIS XML file already states that it is a ${kind} and which transmission it refers ` +
        'to, so it is filed on its own rather than built from this screen. Start a new e-file ' +
        'and upload it there.'
      );
    }
    return null;
  }

  /** Let go of the chosen file and everything read from it. */
  function clearFile() {
    setFile(null);
    setFileError(null);
    setRequestError(null);
    setPercent(null);
    setUploadId(null);
    setInlineResult(null);
  }

  async function startUpload() {
    const format = file ? formatFromFileName(file.name) : null;
    if (!file || !format) return;
    const nameDecisions = reviewState.names.forUpload();
    setBusy(true);
    setRequestError(null);
    try {
      const created = await tabfile.POST('/v1/uploads', {
        body: {
          fileName: file.name,
          format,
          contentType: contentTypeFor(format),
          sizeBytes: file.size,
          // The amended e-file settles the form and the year, so neither is asked for.
          taxYear: filing.taxYear,
          ...(format === 'csv' || format === 'xlsx' ? { formType: filing.formType } : {}),
          // The upload remembers what it is for, so a draft of it resumes as this
          // correction or replacement rather than as a new e-file (TS-8288).
          amends: { efileId: filing.id, kind },
          // How Resolve Names settled the recipient names; the file is read with them.
          ...(nameDecisions ? { nameDecisions } : {}),
        },
      });
      setUploadId(created.id);
      setPercent(0);
      if (!created.uploadUrl) throw new Error('Tab Service did not return an upload link.');
      await putFile(created.uploadUrl, file, contentTypeFor(format), (progress) => setPercent(progress.percent));
      // The transfer is over. Leaving the progress set kept this screen in its
      // transferring state for good, which is what hid the file review and left the upload
      // button disabled after Back to records on a blocked replacement (TS-8289).
      setPercent(null);
      setStep('checks');
    } catch (error) {
      setRequestError(errorMessage(error));
      setPercent(null);
    } finally {
      setBusy(false);
    }
  }

  async function runInlineCheck() {
    const records = parseRecords();
    if (!records) return;
    setBusy(true);
    setRequestError(null);
    try {
      const result = await tabfile.POST('/v1/checks', {
        body: { formType: filing.formType, taxYear: filing.taxYear, records },
      });
      setInlineResult(result);
      setStep('checks');
    } catch (error) {
      setRequestError(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  async function submit() {
    let body: { uploadId: string } | { records: CanonicalRecord[] };
    if (view === 'file') {
      if (!uploadId) return;
      body = { uploadId };
    } else {
      const records = parseRecords();
      if (!records) return;
      body = { records };
    }

    setBusy(true);
    setRequestError(null);
    try {
      const params = { path: { efileId: filing.id } };
      const created =
        kind === 'correction'
          ? await tabfile.POST('/v1/e-files/{efileId}/corrections', {
              params,
              body,
              idempotencyKey: idempotencyKey.current,
            })
          : await tabfile.POST('/v1/e-files/{efileId}/replacements', {
              params,
              body,
              idempotencyKey: idempotencyKey.current,
            });
      // A correction or replacement drafted from an upload leaves Drafts once filed (TS-8702).
      void queryClient.invalidateQueries({ queryKey: ['uploads'] });
      void queryClient.invalidateQueries({ queryKey: ['e-files'] });
      onSubmitted(created);
    } catch (error) {
      const refused = checkResultFromError(error);
      if (refused) {
        setInlineResult(refused);
        setRequestError('Nothing was filed. The records below were refused.');
      } else {
        setRequestError(errorMessage(error));
      }
    } finally {
      setBusy(false);
    }
  }

  // A refusal from the submit is what the person has to read. It used to sit behind the
  // upload's own check, so a replacement refused by Tab Service still said "Nothing is
  // blocked" with no refused records under it (TS-8287).
  const result = inlineResult ?? (view === 'file' ? (uploadData?.check ?? null) : null);
  const uploadWorking =
    view === 'file' &&
    (!uploadData?.status ||
      uploadData.status === 'awaiting_file' ||
      uploadData.status === 'received' ||
      uploadData.status === 'parsing');
  const blocked = view === 'file' ? uploadData?.status === 'blocked' : result ? !result.ok : false;
  const readyToSubmit = !busy && !blocked && (view === 'file' ? !uploadWorking && Boolean(uploadId) : Boolean(result));
  const transferring = percent !== null;
  const canUpload =
    Boolean(file) && !fileError && !busy && !check.phase && !check.error && Boolean(check.passed) && reviewState.reviewed;

  return (
    // The same two columns as the new e-file wizard: the step on the left, the rail on the
    // right. Below the breakpoint the rail goes first, because what is being amended and how
    // far along it is belong above the step rather than under it.
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start">
      <div className="order-2 flex min-w-0 flex-col gap-6 lg:order-1">
      <NotFiledYet hasDraft={Boolean(uploadId)} />

      <Card>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <p className="text-base text-muted-foreground">
            {view === 'json'
              ? words.jsonHint
              : 'Upload the fixed records as the CSV, Excel or IRS 1220 file your system produces.'}
          </p>
          <Segmented
            ariaLabel="How you are sending records"
            value={view}
            items={[
              { value: 'file', label: 'Upload a file' },
              { value: 'json', label: 'Paste JSON' },
            ]}
            onValueChange={(next) => {
              setView(next as 'file' | 'json');
              setStep('records');
              setInlineResult(null);
              setRequestError(null);
            }}
          />
        </div>
      </Card>

      {step === 'records' ? (
        <>
          <Card>
            <SectionHeader
              title={words.recordsTitle}
              description={words.recordsDescription}
              actions={
                <Pill tone="info">
                  {filing.formType}, tax year {filing.taxYear}
                </Pill>
              }
            />

            {view === 'file' ? (
              <>
                {file ? (
                  <>
                    <FileInput accept={ACCEPTED_EXTENSIONS} onChoose={chooseFile} inputRef={fileInputRef} />
                    <ChosenFile
                      file={file}
                      tag={chosenFormat ? formatMeta[chosenFormat as UploadFormat].tag : null}
                      progress={percent !== null ? { percent } : null}
                      busy={busy}
                      onChange={() => fileInputRef.current?.click()}
                      onRemove={clearFile}
                    />
                  </>
                ) : (
                  <FileDropArea
                    accept={ACCEPTED_EXTENSIONS}
                    hint="CSV, Excel, or an IRS 1220 file"
                    onChoose={chooseFile}
                    inputRef={fileInputRef}
                  />
                )}
                {fileError ? (
                  <div className="mt-5">
                    <ErrorNote title="Could not use that file here" detail={fileError} />
                  </div>
                ) : null}
                {file && !fileError && check.error ? (
                  <div className="mt-5">
                    <ErrorNote title="Could not read that file" detail={check.error} />
                  </div>
                ) : null}
                {file && check.phase ? (
                  <div className="mt-5">
                    <CheckingNote phase={check.phase} />
                  </div>
                ) : null}
                {file && check.passed && !check.phase ? (
                  <div className="mt-5">
                    <PassedNote result={check.passed} uploading={transferring} />
                  </div>
                ) : null}
                {file && check.failed && reviewState.parts.length === 0 ? (
                  <div className="mt-5">
                    <PrecheckIssues result={check.failed} />
                  </div>
                ) : null}
                {file && check.review && !check.phase && !transferring ? (
                  <div className="mt-5">
                    <ReviewParts file={file} review={check.review} state={reviewState} />
                  </div>
                ) : null}
              </>
            ) : (
              <Field label="Records as JSON" hint={words.jsonHint} error={parseError ?? undefined}>
                {(props) => (
                  <Textarea
                    {...props}
                    value={text}
                    spellCheck={false}
                    onChange={(event) => setText(event.target.value)}
                    className="min-h-[260px]"
                  />
                )}
              </Field>
            )}

            {requestError ? (
              <div className="mt-5">
                <ErrorNote title="Nothing was sent" detail={requestError} />
              </div>
            ) : null}

            {view === 'file' ? (
              <ReviewFooter
                state={reviewState}
                transferring={transferring}
                canUpload={canUpload}
                busy={busy}
                phase={check.phase}
                uploadLabel={transferring ? `Uploading ${percent}%` : 'Upload and validate'}
                onUpload={() => void startUpload()}
              />
            ) : (
              <div className="flex justify-end pt-6">
                <Button variant="primary" disabled={busy} onClick={() => void runInlineCheck()}>
                  {busy ? <Spinner /> : <ArrowRight className="h-4 w-4" aria-hidden="true" />}
                  Validate records
                </Button>
              </div>
            )}
          </Card>

          {kind === 'correction' ? (
            <AcceptedRecordsPanel filingId={filing.id} />
          ) : (
            <RejectedTransmissionsPanel filing={filing} />
          )}
        </>
      ) : (
        <>
          <Card>
            <SectionHeader
              title="Validation"
              description={words.submitDescription}
              actions={view === 'file' && uploadData?.status ? <UploadStatusPill status={uploadData.status} /> : null}
            />

            {uploadWorking ? (
              <div className="flex items-center gap-3 py-4">
                <Spinner />
                <p className="text-base text-muted-foreground">Reading the file. This page updates automatically.</p>
              </div>
            ) : view === 'file' && upload.isPending ? (
              <TableSkeleton rows={3} columns={4} />
            ) : (
              <div className="flex flex-col gap-4">
                <LabelValueList>
                  <LabelValue label="Amends e-file">{filing.id}</LabelValue>
                  <LabelValue label="Form">{filing.formType}</LabelValue>
                  <LabelValue label="Tax year">{filing.taxYear}</LabelValue>
                  <LabelValue label="Records ready">
                    {view === 'file' ? formatCount(uploadData?.recordCount ?? 0) : formatCount(result?.fileable ?? 0)}
                  </LabelValue>
                </LabelValueList>
                {result ? (
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <p className="text-base text-gray-3">
                      {result.ok
                        ? 'Nothing is blocked. These records can be submitted.'
                        : 'Some records are blocked. Nothing is filed until they are fixed.'}
                    </p>
                    <CheckSummary result={result} />
                  </div>
                ) : null}
                {blocked ? (
                  <ErrorNote
                    title="These records cannot be submitted as they are"
                    detail="Fix the records listed below and send them again. Nothing has reached the IRS."
                  />
                ) : null}
                {requestError ? <ErrorNote title="Nothing was filed" detail={requestError} /> : null}
              </div>
            )}

            <div className="flex items-center justify-between gap-3 pt-6">
              <Button variant="ghost" onClick={() => setStep('records')}>
                <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                Back to records
              </Button>
              <Button variant="primary" disabled={!readyToSubmit} onClick={() => void submit()}>
                {busy ? <Spinner /> : <Send className="h-4 w-4" aria-hidden="true" />}
                {words.action}
              </Button>
            </div>
          </Card>

          {result ? (
            <Card>
              <SectionHeader
                title="Validation results"
                description="Grouped by issue, with what each code means for this e-file."
              />
              <CheckIssues issues={result.issues} />
            </Card>
          ) : null}
        </>
      )}
      </div>

      <div className="order-1 lg:order-2 lg:sticky lg:top-6">
        <WizardRail
          current={step}
          times={stageTimes}
          stages={amendStages}
          subject={kind === 'correction' ? 'Correction of' : 'Replacement of'}
          formType={`${filing.formType}, ${filing.taxYear}`}
          formatLabel={null}
          upload={uploadData ?? null}
          sizeBytes={file?.size ?? null}
          fileName={file?.name ?? null}
        />
      </div>
    </div>
  );
}
